import React, { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Dialog, Menu, Transition } from "@headlessui/react";
import {
  ArrowUpTrayIcon,
  CheckBadgeIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronUpDownIcon,
  Cog6ToothIcon,
  DocumentDuplicateIcon,
  ClipboardDocumentCheckIcon,
  EyeIcon,
  FolderIcon,
  FolderOpenIcon,
  InboxIcon,
  KeyIcon,
  LockClosedIcon,
  PencilIcon,
  PlusIcon,
  QueueListIcon,
  ShareIcon,
  ShieldCheckIcon,
  Squares2X2Icon,
  TableCellsIcon,
  TrashIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { canBuildVariants, canCreateIn, canSeeTasksIn, canShareFolder, folderTree } from "../../utils/folders";
import { useMediaQuery } from "../../pages/Exams/TestBuilder/ui";
import OverflowMenu from "../ui/OverflowMenu";

const focusRing = "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600";

const ROLE_ICONS = { admin: KeyIcon, moderator: ShieldCheckIcon, expert: CheckBadgeIcon, author: PencilIcon, viewer: EyeIcon };

const outlineButton = `inline-flex h-11 items-center justify-center gap-1.5 whitespace-nowrap rounded-lg bg-white px-3 text-sm font-medium text-slate-700 shadow-sm ring-1 ring-inset ring-slate-300 transition duration-200 hover:bg-slate-50 hover:text-slate-900 md:h-9 max-md:flex-1 ${focusRing}`;
// A secondary action next to the one main button (wide screens; phones have it in "⋯").
const quietButton = `inline-flex h-9 items-center justify-center gap-1.5 whitespace-nowrap rounded-lg px-3 text-sm font-medium text-slate-600 transition duration-200 hover:bg-slate-100 hover:text-slate-900 ${focusRing}`;
const primaryButton = `inline-flex h-11 items-center justify-center gap-1.5 whitespace-nowrap rounded-lg bg-blue-600 px-3.5 text-sm font-medium text-white shadow-sm shadow-blue-600/25 transition duration-200 hover:bg-blue-700 md:h-9 max-md:order-first max-md:flex-1 ${focusRing}`;
const textAction = `inline-flex h-9 shrink-0 items-center gap-1 whitespace-nowrap rounded-lg px-3 text-sm font-medium text-blue-700 transition duration-200 hover:bg-blue-50 ${focusRing}`;

// A folder of the top row, with how many questions it holds (nothing for none).
function FolderPill({ selected, onClick, label, count }) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onClick}
      className={`inline-flex h-9 max-w-full shrink-0 items-center gap-2 whitespace-nowrap rounded-full border px-3.5 text-sm transition duration-200 ${focusRing} ${
        selected
          ? "border-blue-200 bg-blue-50 font-semibold text-blue-700"
          : "border-slate-200 bg-white font-medium text-slate-700 hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900"
      }`}
    >
      <span className="max-w-[16rem] truncate">{label}</span>
      {count > 0 && <span className={`text-xs font-semibold tabular-nums ${selected ? "text-blue-700" : "text-slate-500"}`}>{count}</span>}
    </button>
  );
}

// The folders that hold no questions, out of the row: "N more", a menu of them.
function MoreFolders({ exams, onSelect }) {
  const [t] = useTranslation();
  const buttonRef = useRef(null);
  if (!exams.length) return null;
  return (
    <Menu as="div" className="relative">
      {() => {
        // The list (w-72) opens rightwards from the button, or leftwards where the screen would end first (measured on
        // every render of the menu: opening, closing, the active item).
        const box = buttonRef.current ? buttonRef.current.getBoundingClientRect() : null;
        const leftwards = !!box && box.left + 288 > document.documentElement.clientWidth - 24;
        return (
          <>
            <Menu.Button
              ref={buttonRef}
              className={`inline-flex h-9 items-center gap-1 whitespace-nowrap rounded-full px-3 text-sm font-medium text-slate-600 transition duration-200 hover:bg-slate-100 hover:text-slate-900 ${focusRing}`}
            >
              {t("folders.more_folders", { count: exams.length })}
              <ChevronDownIcon className="h-4 w-4 text-slate-400" aria-hidden="true" />
            </Menu.Button>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Menu.Items
                className={`absolute ${leftwards ? "right-0 origin-top-right" : "left-0 origin-top-left"} z-20 mt-1 max-h-80 w-72 max-w-[calc(100vw-2rem)] overflow-auto rounded-xl bg-white p-1 shadow-lg ring-1 ring-slate-900/5 focus:outline-none`}
              >
                <p className="px-3 pb-1 pt-2 text-xs font-medium text-slate-500">{t("folders.empty_folders")}</p>
                {exams.map((e) => (
                  <Menu.Item key={e.uuid}>
                    {({ active }) => (
                      <button
                        type="button"
                        onClick={() => onSelect(e.uuid)}
                        className={`flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm ${active ? "bg-slate-100 text-slate-900" : "text-slate-700"}`}
                      >
                        <FolderIcon className="h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" />
                        <span className="truncate">{e.name}</span>
                      </button>
                    )}
                  </Menu.Item>
                ))}
              </Menu.Items>
            </Transition>
          </>
        );
      }}
    </Menu>
  );
}

// The user's role in a subject; `long` shows "Your role: Author".
export function RoleBadge({ role, long = false, className = "" }) {
  const [t] = useTranslation();
  if (!role) return null;
  const Icon = ROLE_ICONS[role] || EyeIcon;
  const label = t(`folders.roles.${role}`, { defaultValue: role });
  const title = t("folders.role_title", { role: label });
  return (
    <span
      title={title}
      className={`inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-700 ring-1 ring-inset ring-slate-500/15 ${className}`}
    >
      <Icon className="h-3.5 w-3.5" aria-hidden="true" />
      {long ? title : label}
    </span>
  );
}

// An exam whose tests take only approved questions (approved_only_in_tests; its subjects have it from the exam).
export function ApprovedOnlyBadge({ className = "" }) {
  const [t] = useTranslation();
  return (
    <span
      title={t("folders.approved_only_title")}
      className={`inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-800 ring-1 ring-inset ring-emerald-600/20 ${className}`}
    >
      <LockClosedIcon className="h-3.5 w-3.5" aria-hidden="true" />
      {t("folders.approved_only_badge")}
    </span>
  );
}

function SubjectChip({ subject, selected, onClick, showRole, showCount = true }) {
  const [t] = useTranslation();
  const Icon = ROLE_ICONS[subject.role];
  const roleLabel = subject.role ? t(`folders.roles.${subject.role}`, { defaultValue: subject.role }) : "";
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onClick}
      title={showRole && roleLabel ? t("folders.role_title", { role: roleLabel }) : undefined}
      className={`relative inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-full px-3 text-sm font-medium ring-1 ring-inset transition duration-200 ${focusRing} ${
        selected ? "bg-blue-600 text-white ring-blue-600" : "bg-white text-slate-700 ring-slate-200 hover:bg-slate-50 hover:ring-slate-300"
      }`}
    >
      {showRole && Icon && (
        <>
          <Icon className={`h-3.5 w-3.5 ${selected ? "text-blue-100" : "text-slate-400"}`} aria-hidden="true" />
          <span className="sr-only">{roleLabel}:</span>
        </>
      )}
      {subject.name}
      {showCount && subject.questions_count > 0 && (
        <span className={`text-xs font-semibold tabular-nums ${selected ? "text-blue-100" : "text-slate-400"}`}>{subject.questions_count}</span>
      )}
    </button>
  );
}

// "Question list | Matrix | Tasks" of a subject (Tasks with `showTasks`), and "Subject variant" with `showVariants`.
// Phones share the width out equally: a label that does not fit takes two lines and its pill grows with it (44px at
// least). Under 360px the icons give way, so that the tabs fit (a word is never split).
function ViewTabs({ view, onViewChange, showVariants = false, showTasks = false, className = "" }) {
  const [t] = useTranslation();
  const tabs = [
    ["questions", t("folders.tab_questions"), QueueListIcon],
    ["matrix", t("folders.tab_matrix"), TableCellsIcon],
    showTasks && ["tasks", t("folders.tab_tasks"), ClipboardDocumentCheckIcon],
    showVariants && ["variant", t("folders.tab_variant"), DocumentDuplicateIcon],
  ].filter(Boolean);
  // Three or four tabs do not fit a phone's line as they are: three equal columns (a long label takes two lines),
  // or two rows of two.
  const grid =
    tabs.length === 4 ? "max-md:grid max-md:grid-cols-2 max-md:gap-1" : tabs.length === 3 ? "max-md:grid max-md:grid-cols-3 max-md:gap-1" : "";
  return (
    <div role="group" aria-label={t("folders.tabs_label")} className={`inline-flex shrink-0 rounded-lg bg-slate-100 p-1 max-md:w-full ${grid} ${className}`}>
      {tabs.map(([key, label, Icon]) => {
        const current = view === key;
        return (
          <button
            key={key}
            type="button"
            aria-pressed={current}
            onClick={() => onViewChange(key)}
            className={`inline-flex min-h-[2.75rem] items-center gap-1.5 rounded-md px-3 py-1 text-sm font-medium transition duration-200 max-md:flex-1 max-md:justify-center max-md:text-center md:h-8 md:min-h-0 md:py-0 ${focusRing} ${
              current ? "bg-white text-slate-900 shadow-sm ring-1 ring-slate-200" : "text-slate-600 hover:text-slate-900"
            }`}
          >
            <Icon className={`h-4 w-4 shrink-0 max-[359px]:hidden ${current ? "text-blue-600" : "text-slate-400"}`} aria-hidden="true" />
            {label}
          </button>
        );
      })}
    </div>
  );
}

function SheetOption({ selected, onClick, Icon, label, count, role, nested = false }) {
  const [t] = useTranslation();
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onClick}
      className={`flex min-h-[2.75rem] w-full items-center gap-3 rounded-xl px-3 py-2 text-left text-sm transition duration-200 ${focusRing} ${
        selected ? "bg-blue-50 font-semibold text-blue-700" : `${nested ? "text-slate-700" : "font-medium text-slate-900"} hover:bg-slate-50`
      }`}
    >
      {Icon && <Icon className={`h-5 w-5 shrink-0 ${selected ? "text-blue-600" : "text-slate-400"}`} aria-hidden="true" />}
      <span className="min-w-0 flex-1 truncate">{label}</span>
      {role && (
        <span className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">
          {t(`folders.roles.${role}`, { defaultValue: role })}
        </span>
      )}
      {/* As in the wide row: a folder with no questions shows no count. */}
      {typeof count === "number" && count > 0 && <span className="shrink-0 text-xs font-semibold tabular-nums text-slate-500">{count}</span>}
      {selected && <CheckIcon className="h-4 w-4 shrink-0 text-blue-600" aria-hidden="true" />}
    </button>
  );
}

// Phones: the folders and their subjects in a sheet from the bottom of the screen.
function LocationSheet({ open, onClose, exams, selected, noFolderCount, onChoose, showRoles, showCounts, allLabel, onCreateExam }) {
  const n = (value) => (showCounts ? value ?? undefined : undefined);
  const [t] = useTranslation();
  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog as="div" className="relative z-40" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-slate-900/40" aria-hidden="true" />
        </Transition.Child>
        <div className="fixed inset-0 flex items-end justify-center">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="translate-y-full"
            enterTo="translate-y-0"
            leave="ease-in duration-150"
            leaveFrom="translate-y-0"
            leaveTo="translate-y-full"
          >
            <Dialog.Panel className="flex max-h-[85vh] w-full max-w-lg flex-col rounded-t-2xl bg-white shadow-2xl ring-1 ring-slate-900/5">
              <div className="flex items-center justify-between gap-3 border-b border-slate-100 py-2 pl-4 pr-2">
                <Dialog.Title className="text-base font-semibold text-slate-900">{t("folders.choose_location")}</Dialog.Title>
                <button
                  type="button"
                  onClick={onClose}
                  aria-label={t("buttons.close")}
                  className={`inline-flex h-11 w-11 items-center justify-center rounded-lg text-slate-500 transition duration-200 hover:bg-slate-100 hover:text-slate-900 ${focusRing}`}
                >
                  <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                </button>
              </div>
              <div className="overflow-y-auto overscroll-contain p-2 pb-4">
                <ul className="space-y-0.5">
                  <li>
                    <SheetOption selected={selected === "all"} onClick={() => onChoose("all")} Icon={Squares2X2Icon} label={allLabel} />
                  </li>
                  <li>
                    <SheetOption selected={selected === "null"} onClick={() => onChoose("null")} Icon={InboxIcon} label={t("folders.no_folder")} count={n(noFolderCount)} />
                  </li>
                  {exams.map((exam) => (
                    <li key={exam.uuid}>
                      <SheetOption
                        selected={selected === exam.uuid}
                        onClick={() => onChoose(exam.uuid)}
                        Icon={exam.subjects.some((s) => s.uuid === selected) || selected === exam.uuid ? FolderOpenIcon : FolderIcon}
                        label={exam.name}
                        count={n(exam.questions_count)}
                      />
                      {exam.subjects.length > 0 && (
                        <ul className="mb-1 ml-5 space-y-0.5 border-l border-slate-200 pl-2">
                          {exam.subjects.map((s) => (
                            <li key={s.uuid}>
                              <SheetOption
                                nested
                                selected={selected === s.uuid}
                                onClick={() => onChoose(s.uuid)}
                                label={s.name}
                                count={n(s.questions_count)}
                                role={showRoles ? s.role : null}
                              />
                            </li>
                          ))}
                        </ul>
                      )}
                    </li>
                  ))}
                </ul>
                {onCreateExam && (
                  <button
                    type="button"
                    onClick={() => {
                      onClose();
                      onCreateExam();
                    }}
                    className={`mt-2 flex min-h-[2.75rem] w-full items-center gap-3 rounded-xl px-3 text-sm font-medium text-blue-700 transition duration-200 hover:bg-blue-50 ${focusRing}`}
                  >
                    <PlusIcon className="h-5 w-5" aria-hidden="true" />
                    {t("folders.new_exam")}
                  </button>
                )}
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
}

/**
 * Where the list is: the folders ("All questions", the virtual "No folder" and the exams) and, inside the
 * open exam, its subjects (the folders questions live in), each with the user's role. `folders` is the flat
 * list of GET question_folders/; `selected` is "all", "null" (No folder), an exam's or a subject's uuid
 * (an exam shows the questions of all its subjects). The row keeps to the folders that hold questions and the
 * open one; the empty ones wait in "N more". The open folder gets a header: its name, the user's role and how
 * many questions it holds, and its actions (the main one stands out). Phones choose the folder in a sheet.
 * Actions show by role: `onUpload(subject)` and the create link where the role writes questions,
 * `onShare(subject)` for the subject's moderator and admins; `isAdmin` adds `onCreateExam()`,
 * `onEditExam(exam)` (its settings: name, only approved questions in tests), `onAddSubject(exam)`,
 * `onEditSubject(subject)` (its stream) and `onDeleteFolder(folder)`. An exam whose tests take only approved
 * questions says so in its header (and in its subjects'). With `onViewChange`, a selected subject gets the
 * Question list / Matrix tabs (`view` = "questions" | "matrix"), and with `showVariants` the Subject variant
 * tab ("variant") where the role builds variants.
 * Another list (the Texts tab) passes `showCounts={false}` (the counts are of questions), its own create
 * action, `onCreate(subject)` with `createLabel`, shown where the role creates, and `allLabel` for the top level.
 * `children` render under the folder's header.
 */
export default function FolderBar({
  folders,
  noFolderCount,
  selected,
  onSelect,
  isAdmin = false,
  createLinks = true,
  showCounts = true,
  onCreate,
  createLabel,
  allLabel,
  onCreateExam,
  onEditExam,
  onAddSubject,
  onEditSubject,
  onDeleteFolder,
  onUpload,
  onShare,
  view = "questions",
  onViewChange,
  showVariants = false,
  children,
}) {
  const [t] = useTranslation();
  const wide = useMediaQuery("(min-width: 768px)");
  const [sheetOpen, setSheetOpen] = useState(false);
  // The sheet is the phones' picker: a wider screen closes it, so it does not open again on the way back.
  useEffect(() => {
    if (wide) setSheetOpen(false);
  }, [wide]);
  const { exams, byUuid } = useMemo(() => folderTree(folders), [folders]);
  const selectedFolder = byUuid.get(selected) || null;
  const exam = selectedFolder ? exams.find((e) => e.uuid === (selectedFolder.parent || selectedFolder.uuid)) || null : null;
  // A subject whose exam is not in the list is shown as no folder at all.
  const subject = exam && selectedFolder?.parent ? selectedFolder : null;
  const noFolder = selected === "null";
  // Admins have every role, so their subjects carry no role mark.
  const showRoles = !isAdmin;

  // With the counts known, a folder with no questions leaves the row for "N more" unless it is the open one; the
  // Texts tab (no counts) shows them all.
  const rowExams = showCounts ? exams.filter((e) => e.questions_count !== 0 || e.uuid === exam?.uuid) : exams;
  const moreExams = showCounts ? exams.filter((e) => !rowExams.includes(e)) : [];
  const showNoFolder = !showCounts || noFolder || noFolderCount !== 0;

  const topLabel = allLabel || t("folders.all_questions");
  const title = subject ? subject.name : exam ? exam.name : noFolder ? t("folders.no_folder") : topLabel;
  const count = !showCounts ? null : subject ? subject.questions_count : exam ? exam.questions_count : noFolder ? noFolderCount : null;
  const countText = typeof count === "number" ? t("folders.questions_count", { count }) : "";

  const choose = (value) => {
    setSheetOpen(false);
    onSelect(value);
  };

  const writes = !!subject && canCreateIn(subject.role);
  // Sharing and uploading: buttons on wide screens, the first items of "⋯" on phones (one row: create and "⋯").
  const share = subject && canShareFolder(subject.role) && onShare ? () => onShare(subject) : null;
  const upload = writes && onUpload ? () => onUpload(subject) : null;
  const actions = [];
  if (share && wide) {
    actions.push(
      <button key="share" type="button" onClick={share} className={quietButton}>
        <ShareIcon className="h-4 w-4" aria-hidden="true" />
        {t("folders.share.button")}
      </button>
    );
  }
  if (upload && wide) {
    actions.push(
      <button key="upload" type="button" onClick={upload} className={quietButton}>
        <ArrowUpTrayIcon className="h-4 w-4" aria-hidden="true" />
        {t("folders.upload_from_file")}
      </button>
    );
  }
  if (writes && onCreate) {
    actions.push(
      <button key="create" type="button" onClick={() => onCreate(subject)} className={primaryButton}>
        <PlusIcon className="h-4 w-4" aria-hidden="true" />
        {createLabel}
      </button>
    );
  } else if (writes && createLinks) {
    actions.push(
      <Link key="create" to={`/my-questions/create?folder=${subject.uuid}`} className={primaryButton}>
        <PlusIcon className="h-4 w-4" aria-hidden="true" />
        {t("folders.create_question")}
      </Link>
    );
  }
  // Wide screens add subjects next to the subject chips; phones only from this menu.
  const adminItems = isAdmin
    ? [
        exam && onAddSubject && !wide && { key: "add-subject", label: t("folders.add_subject"), Icon: PlusIcon, onClick: () => onAddSubject(exam) },
        exam && onEditExam && { key: "exam-settings", label: t("folders.exam_settings"), Icon: Cog6ToothIcon, onClick: () => onEditExam(exam) },
        subject && onEditSubject && { key: "subject-settings", label: t("folders.subject_settings"), Icon: Cog6ToothIcon, onClick: () => onEditSubject(subject) },
        subject && onDeleteFolder && { key: "delete-subject", label: t("folders.delete_subject"), Icon: TrashIcon, danger: true, onClick: () => onDeleteFolder(subject) },
        exam && onDeleteFolder && { key: "delete-exam", label: t("folders.delete_exam"), Icon: TrashIcon, danger: true, onClick: () => onDeleteFolder(exam) },
      ]
    : [];
  const menuItems = [
    share && !wide && { key: "share", label: t("folders.share.button"), Icon: ShareIcon, onClick: share },
    upload && !wide && { key: "upload", label: t("folders.upload_from_file"), Icon: ArrowUpTrayIcon, onClick: upload },
    ...adminItems,
  ].filter(Boolean);

  const tabs =
    subject && onViewChange ? (
      <ViewTabs
        view={view}
        onViewChange={onViewChange}
        showVariants={showVariants && canBuildVariants(subject.role)}
        showTasks={canSeeTasksIn(subject.role)}
        className="md:ml-auto"
      />
    ) : null;
  // Phones show the path on the location button, so only a subject (its role, actions and tabs) adds a header.
  const showHeader = (exam || noFolder) && (wide || subject);

  return (
    <section aria-label={t("folders.location_label")} className="rounded-2xl border border-slate-200 bg-white shadow-sm">
      {wide ? (
        <div className="flex flex-wrap items-center gap-2 px-4 py-3">
          <div role="group" aria-label={t("folders.exams_label")} className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
            <span className="mr-1 text-xs font-semibold uppercase tracking-wider text-slate-500" aria-hidden="true">
              {t("folders.label")}
            </span>
            <FolderPill selected={selected === "all"} onClick={() => onSelect("all")} label={topLabel} />
            {showNoFolder && (
              <FolderPill selected={noFolder} onClick={() => onSelect("null")} label={t("folders.no_folder")} count={showCounts ? noFolderCount ?? undefined : undefined} />
            )}
            {rowExams.map((e) => (
              <FolderPill key={e.uuid} selected={exam?.uuid === e.uuid} onClick={() => onSelect(e.uuid)} label={e.name} count={showCounts ? e.questions_count : undefined} />
            ))}
            <MoreFolders exams={moreExams} onSelect={onSelect} />
          </div>
          {isAdmin && onCreateExam && (
            <button type="button" onClick={onCreateExam} className={`ml-auto ${textAction}`}>
              <PlusIcon className="h-4 w-4" aria-hidden="true" />
              {t("folders.new_exam")}
            </button>
          )}
        </div>
      ) : (
        <div className="flex items-center gap-2 p-3">
          <button
            type="button"
            aria-haspopup="dialog"
            onClick={() => setSheetOpen(true)}
            className={`flex min-h-[3.25rem] min-w-0 flex-1 items-center gap-3 rounded-xl bg-blue-50/60 px-3 py-2 text-left ring-1 ring-inset ring-blue-600/15 transition duration-200 hover:bg-blue-50 ${focusRing}`}
          >
            <FolderOpenIcon className="h-5 w-5 shrink-0 text-blue-600" aria-hidden="true" />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-xs text-slate-600">{subject ? exam.name : t("folders.label")}</span>
              <span className="block truncate text-sm font-semibold text-slate-900">
                {title}
                {countText && <span className="font-normal text-slate-600"> · {countText}</span>}
              </span>
            </span>
            <span className="sr-only">{t("folders.choose_location")}</span>
            <ChevronUpDownIcon className="h-5 w-5 shrink-0 text-slate-400" aria-hidden="true" />
          </button>
          {/* An open subject has its own row of actions below; a folder's admin actions sit here. */}
          {!subject && <OverflowMenu items={menuItems} label={t("folders.more_actions")} testId="folder-actions" />}
          <LocationSheet
            open={sheetOpen}
            onClose={() => setSheetOpen(false)}
            exams={exams}
            selected={selected}
            noFolderCount={noFolderCount}
            onChoose={choose}
            showRoles={showRoles}
            showCounts={showCounts}
            allLabel={topLabel}
            onCreateExam={isAdmin ? onCreateExam : undefined}
          />
        </div>
      )}

      {showHeader && (
        <div className="border-t border-slate-100 p-3 md:px-4 md:pb-4 md:pt-3">
          <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
            <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1.5">
              {wide && <h3 className="min-w-0 truncate text-xl font-semibold tracking-tight text-slate-900">{title}</h3>}
              {subject && <RoleBadge role={subject.role} long />}
              {exam?.approved_only_in_tests && <ApprovedOnlyBadge />}
              {wide && countText && <span className="whitespace-nowrap text-sm text-slate-500">{countText}</span>}
            </div>
            {(actions.length > 0 || menuItems.length > 0) && (
              <div className="ml-auto flex flex-wrap items-center justify-end gap-2 max-md:w-full">
                {actions}
                <OverflowMenu items={menuItems} label={t("folders.more_actions")} testId="folder-actions" />
              </div>
            )}
          </div>

          {exam && wide && (
            <div className="mt-4 flex flex-wrap items-center gap-2">
              {exam.subjects.length > 0 ? (
                <div role="group" aria-label={t("folders.subjects_label")} className="flex min-w-0 flex-wrap items-center gap-1.5">
                  <span className="mr-1 text-sm text-slate-500" aria-hidden="true">
                    {t("folders.subjects_label")}:
                  </span>
                  <button
                    type="button"
                    aria-pressed={!subject}
                    onClick={() => onSelect(exam.uuid)}
                    className={`inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-full px-3 text-sm font-medium ring-1 ring-inset transition duration-200 ${focusRing} ${
                      !subject ? "bg-blue-600 text-white ring-blue-600" : "bg-white text-slate-700 ring-slate-200 hover:bg-slate-50 hover:ring-slate-300"
                    }`}
                  >
                    {t("folders.all_subjects")}
                    {showCounts && exam.questions_count > 0 && (
                      <span className={`text-xs font-semibold tabular-nums ${!subject ? "text-blue-100" : "text-slate-400"}`}>{exam.questions_count}</span>
                    )}
                  </button>
                  {exam.subjects.map((s) => (
                    <SubjectChip
                      key={s.uuid}
                      subject={s}
                      selected={subject?.uuid === s.uuid}
                      onClick={() => onSelect(s.uuid)}
                      showRole={showRoles}
                      showCount={showCounts}
                    />
                  ))}
                  {isAdmin && onAddSubject && (
                    <button type="button" onClick={() => onAddSubject(exam)} className={`${textAction} h-8 px-2`}>
                      <PlusIcon className="h-4 w-4" aria-hidden="true" />
                      {t("folders.add_subject")}
                    </button>
                  )}
                </div>
              ) : (
                <div className="flex flex-wrap items-center gap-3">
                  <p className="text-sm text-slate-500">
                    {t("folders.exam_empty")} {isAdmin && t("folders.exam_empty_admin")}
                  </p>
                  {isAdmin && onAddSubject && (
                    <button type="button" onClick={() => onAddSubject(exam)} className={outlineButton}>
                      <PlusIcon className="h-4 w-4" aria-hidden="true" />
                      {t("folders.add_subject")}
                    </button>
                  )}
                </div>
              )}
              {tabs}
            </div>
          )}
          {!wide && tabs && <div className="mt-3">{tabs}</div>}
          {exam && children}
        </div>
      )}
    </section>
  );
}
